import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  DataTable,
  Select,
  TextInput,
  toast
} from '@etherisc/ui-kit';
import { RefreshCw, CheckCircle, AlertTriangle, Plus } from 'lucide-react';
import { usePaymentBatches, useProcessBatch, useCreatePaymentBatch } from '../../hooks/insurance/useInsuranceBatch';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { Modal } from '../../components/ledger/Modal';
import type { PaymentBatchRequest } from '../../types/Insurance';

export const BatchReconciliationView = () => {
  const [filters, setFilters] = useState({
    provider: '',
    status: '',
    currency: ''
  });
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState<PaymentBatchRequest>({
    provider: 'stripe',
    externalBatchId: '',
    currency: 'EUR',
    totalAmount: '',
    feeAmount: '0',
    paymentCount: 0,
    batchDate: new Date().toISOString().split('T')[0],
    payments: []
  });

  const { data: batchData, refetch, isLoading } = usePaymentBatches(filters);
  const processBatch = useProcessBatch();
  const createBatch = useCreatePaymentBatch();

  const batches = batchData?.batches || [];

  const columns = [
    {
      accessorKey: 'externalBatchId',
      header: 'Batch ID',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { externalBatchId: string };
        return (
          <span className="font-mono text-sm">{batch.externalBatchId}</span>
        );
      },
    },
    {
      accessorKey: 'provider',
      header: 'Provider',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { provider: string };
        return (
          <div className="flex items-center gap-2">
            <span className="capitalize">{batch.provider}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { totalAmount: string; currency: string };
        return (
          <span className="font-semibold">
            {formatCurrency(batch.totalAmount, batch.currency)}
          </span>
        );
      },
    },
    {
      accessorKey: 'feeAmount',
      header: 'Fees',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { feeAmount: string; currency: string };
        return (
          <span className="text-muted-foreground">
            {formatCurrency(batch.feeAmount, batch.currency)}
          </span>
        );
      },
    },
    {
      accessorKey: 'netAmount',
      header: 'Net Amount',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { netAmount: string; currency: string };
        return (
          <span className="font-semibold text-green-600">
            {formatCurrency(batch.netAmount, batch.currency)}
          </span>
        );
      },
    },
    {
      accessorKey: 'paymentCount',
      header: 'Payments',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { paymentCount: number };
        return (
          <span className="text-center">{batch.paymentCount}</span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { status: string };
        const status = batch.status;
        const variant = status === 'reconciled' ? 'success' :
                      status === 'received' ? 'default' :
                      status === 'failed' ? 'destructive' : 'warning';
        
        const icon = status === 'reconciled' ? <CheckCircle className="h-3 w-3" /> :
                    status === 'failed' ? <AlertTriangle className="h-3 w-3" /> : null;
        
        return (
          <Badge variant={variant} className="flex items-center gap-1">
            {icon}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'batchDate',
      header: 'Batch Date',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { batchDate: string };
        return formatDate(batch.batchDate);
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: { row: { original: unknown } }) => {
        const batch = row.original as { id: string; status: string; journalId?: string };
        return (
          <div className="flex items-center gap-2">
            {batch.status === 'pending' && (
              <Button
                size="sm"
                onClick={() => handleProcessBatch(batch.id)}
                disabled={processBatch.isPending}
              >
                {processBatch.isPending ? 'Processing...' : 'Process'}
              </Button>
            )}
            {batch.status === 'reconciled' && batch.journalId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/ledger/journals/${batch.journalId}`, '_blank')}
              >
                View Journal
              </Button>
            )}
          </div>
        );
      },
    }
  ];

  const handleProcessBatch = async (batchId: string) => {
    try {
      await processBatch.mutateAsync(batchId);
      toast.success('Batch processed successfully');
    } catch (error) {
      toast.error(`Failed to process batch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleCreateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await createBatch.mutateAsync(createFormData);
      toast.success('Payment batch created successfully');
      setShowCreateForm(false);
      setCreateFormData({
        provider: 'stripe',
        externalBatchId: '',
        currency: 'EUR',
        totalAmount: '',
        feeAmount: '0',
        paymentCount: 0,
        batchDate: new Date().toISOString().split('T')[0],
        payments: []
      });
    } catch (error) {
      toast.error(`Failed to create batch: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const updateCreateForm = (field: keyof PaymentBatchRequest) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const actualValue = typeof value === 'string' ? value : value.target.value;
    setCreateFormData(prev => ({ ...prev, [field]: actualValue }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Payment Batch Reconciliation</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Batch
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="col-span-full text-sm font-medium text-gray-700 mb-2">Filter Batches</h3>
            
            <Select
              label="Provider"
              value={filters.provider || 'none'}
              onValueChange={(value) => setFilters(prev => ({ 
                ...prev, 
                provider: value === 'none' ? '' : value 
              }))}
              options={[
                { value: 'none', label: 'All Providers' },
                { value: 'stripe', label: 'Stripe' },
                { value: 'paypal', label: 'PayPal' },
                { value: 'bank_transfer', label: 'Bank Transfer' }
              ]}
            />

            <Select
              label="Status"
              value={filters.status || 'none'}
              onValueChange={(value) => setFilters(prev => ({ 
                ...prev, 
                status: value === 'none' ? '' : value 
              }))}
              options={[
                { value: 'none', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'received', label: 'Received' },
                { value: 'reconciled', label: 'Reconciled' },
                { value: 'failed', label: 'Failed' }
              ]}
            />

            <Select
              label="Currency"
              value={filters.currency || 'none'}
              onValueChange={(value) => setFilters(prev => ({ 
                ...prev, 
                currency: value === 'none' ? '' : value 
              }))}
              options={[
                { value: 'none', label: 'All Currencies' },
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' }
              ]}
            />

            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ provider: '', status: '', currency: '' })}
              >
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Batch Table */}
          <DataTable
            data={batches}
            columns={columns}
            loading={isLoading}
          />

          {batches.length === 0 && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              No payment batches found. Create a new batch to get started.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Batch Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Payment Batch"
      >
        <form onSubmit={handleCreateBatch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Provider"
              value={createFormData.provider}
              onValueChange={updateCreateForm('provider')}
              required
              options={[
                { value: 'stripe', label: 'Stripe' },
                { value: 'paypal', label: 'PayPal' },
                { value: 'bank_transfer', label: 'Bank Transfer' },
                { value: 'check', label: 'Check' }
              ]}
            />

            <TextInput
              label="External Batch ID"
              value={createFormData.externalBatchId}
              onChange={updateCreateForm('externalBatchId')}
              placeholder="batch_1234567890"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput
              label="Total Amount"
              value={createFormData.totalAmount}
              onChange={updateCreateForm('totalAmount')}
              placeholder="0.00"
              required
            />

            <TextInput
              label="Fee Amount"
              value={createFormData.feeAmount}
              onChange={updateCreateForm('feeAmount')}
              placeholder="0.00"
            />

            <Select
              label="Currency"
              value={createFormData.currency}
              onValueChange={updateCreateForm('currency')}
              required
              options={[
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              label="Payment Count"
              type="number"
              value={createFormData.paymentCount.toString()}
              onChange={(e) => setCreateFormData(prev => ({ 
                ...prev, 
                paymentCount: parseInt(e.target.value) || 0 
              }))}
              required
            />

            <TextInput
              label="Batch Date"
              type="date"
              value={createFormData.batchDate}
              onChange={updateCreateForm('batchDate')}
              required
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowCreateForm(false)}
              disabled={createBatch.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={createBatch.isPending}
            >
              {createBatch.isPending ? 'Creating...' : 'Create Batch'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
